<template>
    <div>
        <Head :title="__(title)" />
        <div class="bg-white rounded-md shadow overflow-x-auto">
            <DataTable
                :value="tickets.data"
                removableSort selectionMode="single"
                @row-click="navigateToTicket"
                resizableColumns>
                <template #header>
                    <div class="flex justify-between gap-2">
                        <select-input v-if="!(hidden_fields && hidden_fields.includes('ticket_type'))" v-model="form.type_id" class=" w-full">
                        <option :value="null">{{ __('Type') }}</option>
                        <option v-for="s in types" :key="s.id" :value="s.id">{{ s.name }}</option>
                        </select-input>
                        <select-input v-if="!(hidden_fields && hidden_fields.includes('category'))" v-model="form.category_id" class=" w-full">
                            <option :value="null">{{ __('Category') }}</option>
                            <option v-for="s in categories" :key="s.id" :value="s.id">{{ s.name }}</option>
                        </select-input>
                        <select-input v-if="!(hidden_fields && hidden_fields.includes('department'))" v-model="form.department_id" class=" w-full">
                            <option :value="null">{{ __('Department') }}</option>
                            <option v-for="s in departments" :key="s.id" :value="s.id">{{ s.name }}</option>
                        </select-input>
                        <select-input v-model="form.priority_id" class="  w-full">``
                            <option :value="null">{{ __('Priority') }}</option>
                            <option v-for="s in priorities" :key="s.id" :value="s.id">{{ s.name }}</option>
                        </select-input>
                        <select-input v-model="form.status_id" class=" w-full">
                            <option :value="null">{{ __('Status') }}</option>
                            <option v-for="s in statuses" :key="s.id" :value="s.id">{{ s.name }}</option>
                        </select-input>
                        <select-input-filter :placeholder="__('Assign To')" :onInput="doFilter" @focus="doFilter" :items="assignees"
                                            v-if="!(hidden_fields && hidden_fields.includes('assigned_to')) && user_access.ticket.update"
                                            v-model="form.assigned_to" class="w-full">
                        </select-input-filter>
                    </div>
                    <div class="flex justify-between mt-2 ">
                        <search-input v-model="form.search" class="w-1/2 search" @reset="reset"></search-input>
                        <Link class="btn-indigo flex items-center" :href="this.route('tickets.create')">
                            <span>{{ __('New Ticket') }}</span>
                        </Link>
                    </div>
                </template>
                <template #empty> {{ __('No tickets found.') }} </template>
                <Column field="uid" header="ID" :sortable="true"></Column>
                <Column field="user" header="Customer" :sortable="true">
                    <template #body="slotProps">
                            <span class="text-sm items-center">
                                {{ slotProps.data.user }}
                            </span>
                    </template>
                </Column>
                <Column field="subject" header="Subject" :sortable="true">
                    <template #body="slotProps">
                        <Link href="" class="s__details flex flex-col max-w-[200px] md:max-w-[500px] 2xl:max-w-[700px]">
                            <span class="subject_t truncate">
                                <Tag v-if="slotProps.data.type" :value="slotProps.data.type" severity="secondary" rounded/>
                                <span class="text-gray-600 font-medium">{{ slotProps.data.subject }}</span>
                            </span>
                            <span v-if="slotProps.data.details" class="text-sm items-center pt-1 text-gray-400 truncate" title="Details">
                                {{ stripHtml(slotProps.data.details) }}
                            </span>
                        </Link>
                    </template>
                </Column>
                <Column field="priority" header="Priority" :sortable="true">
                    <template #body="slotProps">
                            <div v-if="slotProps.data.priority" class="w-full">
                                <button :class="[getPriorityColor(slotProps.data.priority), 'w-full px-2 py-2 rounded-full']">{{ slotProps.data.priority }}</button>
                            </div>
                    </template>
                </Column>
                <Column field="assigned_to" header="Assigned" :sortable="true">
                    <template #body="slotProps">
                            <div v-if="slotProps.data.assigned_to" class="w-full flex justify-center">
                                <Avatar v-if="slotProps.data.assigned_to" :label="getAvatarLabel(slotProps.data.assigned_to.first_name, slotProps.data.assigned_to.last_name)"
                                    v-tooltip="slotProps.data.assigned_to.first_name + ' ' + slotProps.data.assigned_to.last_name + ' ' + '(' + slotProps.data.assigned_to.email + ')'" :style="{ backgroundColor: getAvatarColor(slotProps.data.assigned_to.email) }" shape="circle"/>
                            </div>
                    </template>
                </Column>
                <Column field="updated_at" header="Updated" :sortable="true">
                    <template #body="slotProps">
                            {{ __('error') === 'error' ? moment(slotProps.data.updated_at).fromNow(true) : moment(slotProps.data.updated_at).locale('zh-tw').fromNow(true) }}
                    </template>
                </Column>
            </DataTable>
        </div>
        <div class="flex justify-between items-center mt-4">
            <pagination class="mt-4" :links="tickets.links" />
            <select v-model="form.limit" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:focus:ring-blue-500 dark:focus:border-blue-500">
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
            </select>
        </div>
    </div>
</template>

<script>
import { Link, Head } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SelectInput from '@/Shared/SelectInput'
import SearchInput from '@/Shared/SearchInput'
import SelectInputFilter from '@/Shared/SelectInputFilter'
import moment from 'moment'
import axios from 'axios'
import Tag from 'primevue/tag';
import Avatar from 'primevue/avatar';
import { getAvatarLabel, getAvatarColor } from '@/helpers/avatarHelpers';
import { getPriorityColor, stripHtml } from "@/helpers/ticketHelpers";
import Panel from 'primevue/panel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';

export default {
    metaInfo: { title: 'Tickets' },
    components: {
        SearchInput,
        Icon,
        Link,
        Head,
        Pagination,
        SelectInputFilter,
        SelectInput,
        Tag,
        Avatar,
        Panel,
        DataTable,
        Column,
        IconField,
        InputIcon,
        InputText,
    },
    layout: Layout,
    props: {
        filters: Object,
        tickets: Object,
        assignees: Array,
        auth: Object,
        title: String,
        priorities: Array,
        statuses: Array,
        types: Array,
        categories: Array,
        departments: Array,
        hidden_fields: Object,
    },
    remember: 'form',
    data() {
        return {
            user_access: this.$page.props.auth.user.access,
            form: {
                search: this.filters.search,
                limit: this.filters.limit ?? 10,
                customer_id: this.filters.customer_id,
                field: this.filters.field,
                direction: this.filters.direction,
                priority_id: this.filters.priority_id ?? null,
                status_id: this.filters.status_id ?? null,
                type_id: this.filters.type_id ?? null,
                category_id: this.filters.category_id ?? null,
                department_id: this.filters.department_id ?? null,
            },
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function() {
                // reset pagination to page 1 upon filtering
                const currentRouteParams = route().params;
                currentRouteParams.page = 1;

                this.$inertia.get(route().current(), pickBy(this.form), {replace: true, preserveState: true});
                this.$inertia.get(route(route().current(), currentRouteParams), pickBy(this.form), {replace: true, preserveState: true});
            }, 150),
        },
    },
    methods: {
        doFilter(e){
            axios.get(this.route('filter.assignees', {search: e.target.value})).then((res)=>{
                this.assignees.splice(0, this.assignees.length, ...res.data);
            })
        },
        sort(field) {
            this.form.field = field;
            this.form.direction = this.form.direction === 'asc' ? 'desc' : 'asc';
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
        stripHtml,
        getAvatarLabel,
        getAvatarColor,
        getPriorityColor,
        navigateToTicket(event) {
            const ticketId = event.data.uid || event.data.id;
            this.$inertia.get(this.route('tickets.edit', ticketId));
        },
    },
    created() {
        this.moment = moment;
    }
}
</script>
